import axios from 'axios';

// Configuration Campay
const CAMPAY_BASE_URL = 'https://campay.net/api';
const CAMPAY_API_TOKEN = '0dee99ec7ff910d35a5b55665b04918027f3f8d7';

// En-têtes pour l'authentification
const headers = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
  'Authorization': `Token ${CAMPAY_API_TOKEN}`,
};

class CampayService {
  // Initier un paiement direct avec métadonnées enrichies
  static async initiatePayment(amount, phoneNumber, description, externalReference = '', metadata = {}) {
    try {
      // Formatage du numéro de téléphone
      const formattedPhone = this.formatPhoneNumber(phoneNumber);
      
      // Corps de la requête avec métadonnées
      const requestBody = {
        amount: amount.toString(),
        currency: 'XAF',
        from: formattedPhone,
        description: description || 'Paiement via application',
        external_reference: externalReference || `ORDER-${Date.now()}`,
        // Ajout des métadonnées personnalisées
        metadata: {
          ...metadata,
          app_name: 'DocsApp',
          timestamp: new Date().toISOString(),
        }
      };
      
      console.log('En-têtes de la requête:', headers);
      console.log('Corps de la requête avec métadonnées:', requestBody);
      
      // Envoi de la requête POST à /collect
      const response = await axios.post(`${CAMPAY_BASE_URL}/collect/`, requestBody, { headers });
      
      console.log('Réponse initiation paiement:', response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'initiation du paiement:', 
                   error.response?.data || error.message);
      throw error;
    }
  }
  
  // Vérifier le statut d'une transaction
  static async checkPaymentStatus(referenceId) {
    try {
      console.log('Vérification du statut pour la référence:', referenceId);
      
      if (!referenceId) {
        throw new Error('Référence de transaction non fournie');
      }
      
      // Envoi de la requête GET à /transaction/{reference}/
      const response = await axios.get(`${CAMPAY_BASE_URL}/transaction/${referenceId}/`, { headers });
      
      console.log('Réponse statut de la transaction:', response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la vérification du statut:', 
                   error.response?.data || error.message);
      throw error;
    }
  }
  
  // Déterminer si un paiement est complété avec succès
  static isPaymentComplete(status) {
    return status === 'SUCCESSFUL' || status === 'SUCCESS';
  }
  
  // Déterminer si un paiement est en attente
  static isPaymentPending(status) {
    return status === 'PENDING';
  }
  
  // Déterminer si un paiement a échoué ou a été annulé
  static isPaymentFailed(status) {
    return ['FAILED', 'CANCELLED', 'EXPIRED', 'REVERSED'].includes(status);
  }
  
  // Générer un lien de paiement avec métadonnées
  static async generatePaymentLink(amount, description, externalReference, firstName = '', lastName = '', email = '', metadata = {}) {
    try {
      const requestBody = {
        amount: amount.toString(),
        currency: 'XAF',
        description: description,
        external_reference: externalReference || `ORDER-${Date.now()}`,
        first_name: firstName,
        last_name: lastName,
        email: email,
        redirect_url: 'https://example.com', // Remplacez par votre URL de redirection
        payment_options: 'MOMO',
        // Métadonnées personnalisées
        metadata: {
          ...metadata,
          app_name: 'DocsApp',
          timestamp: new Date().toISOString(),
        }
      };
      
      const response = await axios.post(`${CAMPAY_BASE_URL}/get_payment_link/`, requestBody, { headers });
      console.log('Lien de paiement généré:', response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la génération du lien de paiement:', 
                   error.response?.data || error.message);
      throw error;
    }
  }
  
  // Créer des métadonnées enrichies pour une transaction
  static createTransactionMetadata(document, documentDetail, userId) {
    const metadata = {
      user_id: userId,
      document_id: document.id,
      document_type: document.type,
      purchase_timestamp: new Date().toISOString(),
    };
    
    if (document.type === 'concours') {
      metadata.concours_sigle = document.sigle || 'N/A';
      metadata.concours_nom = document.nomComplet || 'N/A';
      metadata.document_titre = documentDetail.titre || 'N/A';
      metadata.document_filiere = documentDetail.filiere || 'N/A';
      metadata.document_annee = documentDetail.annee?.toString() || 'N/A';
      metadata.document_prix = documentDetail.prix?.toString() || 'N/A';
      metadata.nombre_pages = documentDetail.nombrePages?.toString() || 'N/A';
      metadata.is_gratuit = documentDetail.gratuit ? 'true' : 'false';
      
      if (documentDetail.groupId) {
        metadata.group_id = documentDetail.groupId.toString();
      }
    } else if (document.type === 'bords') {
      metadata.bord_titre = document.titre || 'N/A';
      metadata.document_prix = document.prix?.toString() || 'N/A';
      metadata.nombre_pages = document.nombrePages?.toString() || 'N/A';
      metadata.langue = document.langue || 'fr';
    } else {
      // Document simple
      metadata.document_titre = document.titre || 'N/A';
    }
    
    return metadata;
  }
  
  // Générer une description enrichie pour CamPay
  static generateEnrichedDescription(document, documentDetail) {
    // Objectif : que la description CamPay corresponde EXACTEMENT à ce que
    // l'utilisateur voit dans l'app (titre + année + liste complète des matières),
    // afin de pouvoir retrouver précisément le document acheté en cas de litige.
    // Format concours : "SIGLE: Titre - Année (Matière1, Matière2, ...)".
    // Le champ `filiere` contient déjà la liste complète des matières.
    const detail = documentDetail || {};
    let description = "";

    if (document.type === 'concours') {
      // Sigle du concours parent pour l'identification (ex: "IDE (MINSANTE)")
      const sigle = document.sigle || document.nomComplet || 'Concours';
      const titre = (detail.titre || 'Document').trim();
      const filiere = (detail.filiere || '').replace(/\s+/g, ' ').trim();
      const annee = detail.annee || '';

      description = `${sigle}: ${titre}`;
      if (annee) description += ` - ${annee}`;
      if (filiere) description += ` (${filiere})`;
    }
    else if (document.type === 'bords') {
      const titre = (document.titre || 'Bord').trim();
      description = `Bord: ${titre}`;
    }
    else {
      // Document simple
      const titre = (document.titre || 'Document').trim();
      description = `Doc: ${titre}`;
    }

    // Nettoyer les espaces superflus.
    description = description.replace(/\s+/g, ' ').trim();

    // Limite de l'API CamPay confirmée par test : au-delà de ~200 caractères,
    // CamPay rejette la requête (HTTP 500). On tronque à 200 avec "..." pour
    // indiquer que le nom est plus long. Les noms réalistes (~110 car.) passent largement.
    if (description.length > 200) {
      description = description.substring(0, 197) + '...';
    }

    return description;
  }
  
  // Générer une référence externe enrichie
  static generateEnrichedReference(document, documentDetail, userId) {
    const timestamp = Date.now();
    const userPrefix = userId ? userId.substring(0, 8) : 'USER';
    
    let docType = '';
    let docId = '';
    
    if (document.type === 'concours') {
      docType = 'CONC';
      docId = document.sigle ? document.sigle.toUpperCase().substring(0, 4) : 'EXAM';
    } else if (document.type === 'bords') {
      docType = 'BORD';
      docId = document.titre ? document.titre.toUpperCase().replace(/[^A-Z]/g, '').substring(0, 4) : 'PREP';
    } else {
      docType = 'DOC';
      docId = document.titre ? document.titre.toUpperCase().replace(/[^A-Z]/g, '').substring(0, 4) : 'FILE';
    }
    
    // S'assurer que docId a au moins 4 caractères
    if (docId.length < 4) {
      docId = docId.padEnd(4, 'X');
    }
    
    return `${docType}-${docId}-${userPrefix}-${timestamp}`;
  }
  
  // Formater un numéro de téléphone pour Campay
  static formatPhoneNumber(phone) {
    if (!phone) return '';
    
    // Retirer les espaces, tirets et autres caractères non numériques
    let cleaned = phone.replace(/[^\d+]/g, '');
    
    // Retirer le + si présent
    if (cleaned.startsWith('+')) {
      cleaned = cleaned.substring(1);
    }
    
    // Ajouter le préfixe 237 (Cameroun) si non présent
    if (!cleaned.startsWith('237') && cleaned.length <= 9) {
      cleaned = '237' + cleaned;
    }
    
    return cleaned;
  }
  
  // Valider les données avant envoi
  static validatePaymentData(amount, phone, description) {
    const errors = [];
    
    if (!amount || amount <= 0) {
      errors.push('Montant invalide');
    }
    
    if (!phone || phone.length < 9) {
      errors.push('Numéro de téléphone invalide');
    }
    
    if (!description || description.trim().length === 0) {
      errors.push('Description manquante');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
  
  // Logger pour le suivi des transactions
  static logTransaction(action, data, result = null, error = null) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      action,
      data,
      result,
      error: error ? error.message : null
    };
    
    console.log('CamPay Transaction Log:', logEntry);
    
    // Ici vous pourriez envoyer les logs vers votre service de monitoring
    // ou les stocker dans votre base de données pour le suivi
  }
}

export default CampayService;